/**
 * Toast — sistema simple de notificaciones para el portal.
 * Provider global + hook `useToast()`. Reemplaza alert() viejos.
 *
 * v8.5.49 · piel · único carril de feedback: un solo toast visible — el
 * nuevo reemplaza al anterior (antes se apilaban en un array). Sin íconos
 * de check ni colores por tipo: fondo --ink/texto --surface siempre, la
 * distinción success/error/info/warn queda solo en el texto del mensaje.
 * `tipo` se conserva en la API (toast.success/error/info/warn) porque son
 * las llamadas que usa el resto del portal — internamente ya no cambia el
 * look. Acción opcional (opts.actionLabel/opts.onAction) es aditivo: nadie
 * la usaba antes, ningún call-site existente se rompe por no pasarla.
 */
import { createContext, useContext, useState, useCallback, useRef } from "react";

const ToastContext = createContext({ toast: { success: () => {}, error: () => {}, info: () => {}, warn: () => {} } });

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }) {
  const [current, setCurrent] = useState(null);
  const timerRef = useRef(null);

  const remove = useCallback(() => setCurrent(null), []);

  const push = useCallback((tipo, mensaje, opts = {}) => {
    const id = Math.random().toString(36).slice(2, 10);
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrent({ id, tipo, mensaje, actionLabel: opts.actionLabel, onAction: opts.onAction });
    const duration = opts.duration ?? DEFAULT_DURATION;
    if (duration > 0) timerRef.current = setTimeout(remove, duration);
    return id;
  }, [remove]);

  const toast = {
    success: (m, opts) => push("success", m, opts),
    error:   (m, opts) => push("error", m, opts),
    info:    (m, opts) => push("info", m, opts),
    warn:    (m, opts) => push("warn", m, opts),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* v8.5.49 · piel · abajo a la derecha en desktop; en mobile abajo
          centrado, por encima de la bottom nav (bottom-16 despeja sus 56px +
          margen, mismo criterio que las action bars sticky del portal). */}
      {current && (
        <div className="fixed inset-x-4 bottom-16 sm:inset-x-auto sm:bottom-6 sm:right-6 z-[var(--z-notice)] flex justify-center sm:justify-end pointer-events-none">
          <ToastItem key={current.id} {...current} onClose={remove} />
        </div>
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ mensaje, actionLabel, onAction, onClose }) {
  return (
    <div className="pointer-events-auto max-w-sm flex items-center gap-3 rounded-[14px] bg-[var(--ink)] px-3 py-2.5"
         style={{ animation: "slideIn 0.2s ease-out" }}>
      <div className="text-[15px] text-[var(--surface)] flex-1 break-words">{mensaje}</div>
      {actionLabel && (
        <button onClick={() => { onAction?.(); onClose(); }}
                className="text-[15px] text-[var(--surface)] underline hover:opacity-80 flex-shrink-0 whitespace-nowrap">
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
